// i18n
import translations from './i18n.json';
import { translateInComponent } from 'infras/i18n';

import styled from '@emotion/styled';
import ServicePageTemplate from 'presentations/views/components/templates/ServicePageTemplate';
import ScreenSlideList from 'presentations/views/components/organisms/service/ScreenSlideList';
import OddSectionContainer from 'presentations/views/components/organisms/service/OddSectionContainer';
import {
  background,
  black,
  gray03,
  gray04,
  whiteFix,
} from 'presentations/views/styles/color';
import { imagePath } from 'presentations/assets';
import Header from 'presentations/views/components/organisms/Header';
import Headline from 'presentations/views/components/atoms/Headline';
import Icon from 'presentations/views/components/atoms/Icon';
import FeatureSchedule from 'presentations/views/components/organisms/service/FeatureSchedule';
import FeatureDesign from 'presentations/views/components/organisms/service/FeatureDesign';
import FeatureServicePayment from 'presentations/views/components/organisms/service/FeatureServicePayment';
import TopBooking from 'presentations/views/components/organisms/service/TopBooking';
import TopDesign from 'presentations/views/components/organisms/service/TopDesign';
import TopScheduling from 'presentations/views/components/organisms/service/TopScheduling';
import FeatureSecurity from 'presentations/views/components/organisms/service/FeatureSecurity';
import IntegrationList from 'presentations/views/components/organisms/service/ServiceIntegrationList';
import ReviewList from 'presentations/views/components/organisms/service/ServiceReviewList';
import { ViewerItem } from 'presentations/api/schema/user';
import {
  borderRadius,
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import MainVisual from 'presentations/views/components/organisms/service/MainVisual';
import * as tracker from 'presentations/views/helpers/analytics';
import ImageSectionContainer from 'presentations/views/components/organisms/service/ImageSectionContainer';

interface Props {
  viewer: ViewerItem | null;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const onClickInquiry = () => {
    tracker.setEvent('click corporative inquiry', {
      source: 'jicoo',
      product: 'booking',
    });
  };
  return (
    <ServicePageTemplate
      isVisibleSupport={true}
      header={<Header viewer={props.viewer} />}
      breadcrumbs={[]}
    >
      <MainVisual
        title={t('page_title.0')}
        description={t('page_title.1')}
        onClickInquiry={onClickInquiry}
        viewer={props.viewer}
        size="large"
        isShowSignUp
      />
      <Page>
        <SlideContainer>
          <ScreenSlideList />
        </SlideContainer>
        <AnchorNavigationContainer>
          <AnchorNavigationTitle variant="h3">
            {t('features')}
          </AnchorNavigationTitle>
          <AnchorNavigation>
            <AnchorButton href="#booking">
              {t('booking')}
              <AnchorButtonIcon name="expand_more" />
            </AnchorButton>
            <AnchorButton href="#schedule">
              {t('schedule')}
              <AnchorButtonIcon name="expand_more" />
            </AnchorButton>
            <AnchorButton href="#payment">
              {t('payment')}
              <AnchorButtonIcon name="expand_more" />
            </AnchorButton>
            <AnchorButton href="#design">
              {t('design')}
              <AnchorButtonIcon name="expand_more" />
            </AnchorButton>
            <AnchorButton href="#calendar">
              {t('calendar')}
              <AnchorButtonIcon name="expand_more" />
            </AnchorButton>
            <AnchorButton href="#security">
              {t('security')}
              <AnchorButtonIcon name="expand_more" />
            </AnchorButton>
            <AnchorButton href="#integration">
              {t('integration')}
              <AnchorButtonIcon name="expand_more" />
            </AnchorButton>
          </AnchorNavigation>
        </AnchorNavigationContainer>

        <SectionContainerWrapper>
          <OddSectionContainer>
            <ImageSectionContainer id="booking">
              <TopBooking />
            </ImageSectionContainer>
          </OddSectionContainer>
          <ImageSectionContainer id="schedule">
            <TopScheduling
              title={t('schedule title')}
              subTitle={t('schedule sub title')}
            />
          </ImageSectionContainer>
          <OddSectionContainer>
            <ImageSectionContainer id="design">
              <TopDesign />
            </ImageSectionContainer>
          </OddSectionContainer>
          <SectionContainer id="payment">
            <FeatureServicePayment />
          </SectionContainer>
          <OddSectionContainer>
            <SectionContainer>
              <FeatureDesign />
            </SectionContainer>
          </OddSectionContainer>
          <SectionContainer id="security">
            <FeatureSecurity />
          </SectionContainer>
        </SectionContainerWrapper>
        <SectionContainer id="integration">
          <IntegrationList />
        </SectionContainer>
        <SectionContainer>
          <ReviewList />
        </SectionContainer>
      </Page>
    </ServicePageTemplate>
  );
}

const Page = styled.div``;
const SectionContainerWrapper = styled.div`
  background: url(${imagePath('pages/top/services/bg01.png')}) center 8vw
    repeat-y;
  background-size: 100vw auto;
`;
const SectionContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: ${spacing(128)} ${spacing(16)} ${spacing(96)};
  ${mediaMax.tablet} {
    padding: ${spacing(48)} ${spacing(16)};
  }
  border-bottom: 1px solid ${gray03};
  :last-of-type {
    /* border-bottom: 0; */
  }
`;
const AnchorNavigationContainer = styled.div`
  padding: ${spacing(24)} ${spacing(16)};
`;
const AnchorNavigation = styled.div`
  margin-top: ${spacing(20)};
  display: flex;
  justify-content: center;
  ${mediaMax.tablet} {
    flex-wrap: wrap;
  }
`;
const AnchorButton = styled.a`
  display: inline-flex;
  padding: ${spacing(8)} 0;
  margin-right: ${spacing(16)};
  width: 146px;
  text-align: center;
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(16)};
  flex-direction: column;
  justify-content: center;
  border: 1px solid ${black};
  color: ${black};
  border-radius: ${borderRadius(6)};
  background-color: ${background};
  :hover {
    background-color: ${gray04};
    color: ${whiteFix};
  }
  :last-of-type {
    margin-right: 0;
  }
  ${mediaMax.tablet} {
    width: 108px;
    font-size: ${fontSize(12)};
    padding: ${spacing(4)} 0;
    margin-right: ${spacing(8)};
    margin-bottom: ${spacing(12)};
    white-space: nowrap;
  }
`;
const AnchorButtonIcon = styled(Icon)`
  margin: 0 auto;
  ${mediaMax.tablet} {
    width: 16px !important;
    height: 16px !important;
  }
`;

const AnchorNavigationTitle = styled(Headline)`
  text-align: center;
`;

const SlideContainer = styled.div`
  margin-bottom: ${spacing(48)};
  text-align: center;
`;
